import { List, Map } from 'immutable'
import i18next from 'i18next'
import { createSelector } from 'reselect'
import { EOrganizationType } from '@prohabits/metaverse'
// @ts-ignore
import { getUserNameOrEmail } from 'helpers/getUserUIData'
import { escapeSpecialSymbols } from 'helpers/patternsRegExp'
// @ts-ignore
import { getPublishedProTracksLibrariesList, getPublishedProtracksList } from 'store/entities/protracks/selectors'
import { organizationId as editingOrganizationIdFromRouter } from 'store/entities/router/selectors'
// @ts-ignore
import { USERS_FILTER } from 'app/constants/organizations'
import { invitesState } from './reducer'
import { CountriesCodes } from 'store/records/organization'
// @ts-ignore
import { USA_ISO } from 'app/constants/phoneCountryCodes'
// @ts-ignore
import { getOrganizationSettingsChildrenList } from 'store/entities/organizationSettings/selectors'
// @ts-ignore
import * as selectorHelpers from 'helpers/selectorHelpers'
import {
  ACTIVITY_DEFAULT_IMAGE_DOMAIN,
  ACTIVITY_DEFAULT_IMAGE_DOMAIN_ID,
  ACTIVITY_DEFAULT_IMAGE_MESSAGE,
  ACTIVITY_DEFAULT_IMAGE_MESSAGE_ID,
} from 'app/constants/activity'
import { IOrganization } from './interfaces/common.interfaces'
import moment from 'moment'
import { WORK_WEEK_DEFAULT } from 'app/constants/profile'

export const getOrganizations = (state: any) => state.get('persist').organizations

export const getPendingObject = createSelector(getOrganizations, organization => organization.get('pendingObject'))

export const getAllOrganizations = createSelector(getOrganizations, organizations => organizations.get('all') || Map())

export const isOrganizationDataLoaded = (orgId: number) =>
  createSelector(getAllOrganizations, (all: any) => all.getIn([orgId, 'loaded']))

export const getAllOrganizationsLabelIds = createSelector(getAllOrganizations, (organizations: any[]) =>
  organizations.reduce((labelIds: number[], organization: any) => {
    return organization.get('labelId') ? labelIds.push(organization.get('labelId')) : labelIds
  }, List())
)

export const getOrganizationsList = createSelector(getAllOrganizations, (organizations: List<IOrganization>) =>
  organizations.reduce((all: any, organization: any) => all.push(organization), List())
)

export const getOrganizationsWithMutliAdminsList = createSelector(
  getOrganizationsList,
  (organizations: Record<string, any>) => {
    return organizations.filter((organization: any) => {
      return organization.get('isChildrenOrganizationExists') && organization.get('isMultiAdminExists')
    })
  }
)

export const getFirstOrganization = createSelector(getOrganizationsList, (organizations: Record<string, any>) =>
  organizations.first()
)

export const getViewOrganizationId = createSelector(getOrganizations, (organizations: Record<string, any>) =>
  organizations.get('view')
)

/**
 * Current organization. Used for Main Header Logo for example
 */

export const getViewOrganization = createSelector(
  getAllOrganizations,
  getViewOrganizationId,
  (organizations: Record<string, any>, id: number) => organizations.get(id.toString())
)

export const getViewOrganizationProtracks = createSelector(getViewOrganization, (organization: any) => {
  const proTracksList = organization.get('proTracksLevels').map((level: any) => level.get('proTrack'))
  return proTracksList.toSet().toList()
})

export const getViewOrganizationReviewsSurveyId = createSelector(
  getViewOrganization,
  (organization: Record<string, any>) => {
    return organization.get('reviewsSurveyId')
  }
)

export const getViewOrganizationEmailProviderApiKey = createSelector(
  getViewOrganization,
  (organization: Record<string, any>) => {
    return organization.get('emailProviderApiKey')
  }
)

export const getViewOrganizationType = createSelector(getViewOrganization, (organization: Record<string, any>) => {
  return organization.get('type')
})

export const isOrganizationIntegratedType = createSelector(
  getViewOrganizationType,
  type => type === EOrganizationType.integrated
)

export const getViewOrganizationEmailFrom = createSelector(getViewOrganization, (organization: Record<string, any>) => {
  return organization.get('emailFrom')
})

export const getViewOrganizationIsApiKeyEnabled = createSelector(
  getViewOrganization,
  (organization: Record<string, any>) => {
    return organization.get('isApiKeyEnabled')
  }
)

export const getViewOrganizationDisplayName = createSelector(
  getViewOrganization,
  (organization: Record<string, any>) => {
    return organization.get('displayName')
  }
)

export const getOrganizationsPagination = createSelector(getOrganizations, (organizations: Record<string, any>) =>
  organizations.get('pagination')
)

export const getOrganizationsCurrentPage = createSelector(getOrganizationsPagination, pagination =>
  pagination.get('current')
)

export const getOrganizationsPerPage = createSelector(getOrganizationsPagination, pagination =>
  pagination.get('perPage')
)

export const getOrganizationsSortCriteria = createSelector(getOrganizations, organizations => organizations.get('sort'))

export const getOrganizationsSortBy = createSelector(getOrganizationsSortCriteria, sort => sort.get('by'))

export const getOrganizationsSortDesc = createSelector(getOrganizationsSortCriteria, sort => sort.get('desc'))

export const getOrganizationsFilterBy = createSelector(getOrganizations, organizations => organizations.get('filter'))

export const getOrganizationFilteredList = createSelector(
  getOrganizationsList,
  getOrganizationsFilterBy,
  (list, filterBy) => {
    return filterBy && filterBy.length > 0
      ? list.filter((organization: Record<string, any>) => {
          const name = organization.get('name') || ''
          const domain = organization.get('domain') || ''
          return `${name}${domain}`.toLowerCase().indexOf(escapeSpecialSymbols(filterBy.toLowerCase())) !== -1
        })
      : list
  }
)

export const getOrganizationFilteredSortedList = createSelector(
  getOrganizationFilteredList,
  getOrganizationsSortBy,
  getOrganizationsSortDesc,
  (list, by, desc) => {
    if (!by) return list

    if (by === 'activeUsers') {
      return list.sortBy(
        (org: Record<string, any>) => org.get('activeUsers'),
        (a: number, b: number) => {
          if (desc ? a > b : a < b) {
            return -1
          }
          if (desc ? a < b : a > b) {
            return 1
          }
          if (a === b) {
            return 0
          }
        }
      )
    }

    if (by === 'status') {
      return list.sortBy(
        (org: Record<string, any>) => org.get('active'),
        (a: number, b: number) => (desc ? b - a : a - b)
      )
    }

    return list.sortBy(
      (org: Record<string, any>) => {
        const itemBy = org.get(by) || '--'
        return itemBy.toUpperCase()
      },
      (a: any, b: any) => (desc ? a.localeCompare(b) : b.localeCompare(a))
    )
  }
)

export const getOrganizationsSortedListFilteredTotal = createSelector(
  getOrganizationFilteredSortedList,
  list => list.size
)

export const getOrganizationsTotalPages = createSelector(
  getOrganizationsSortedListFilteredTotal,
  getOrganizationsPerPage,
  (total, perPage) => Math.ceil(total / perPage)
)

export const getOrganizationsForCurrentPage = createSelector(
  getOrganizationFilteredSortedList,
  getOrganizationsCurrentPage,
  getOrganizationsPerPage,
  selectorHelpers.handleUsersForCurrentPage
)

export const getOrganizationUsers = createSelector(getOrganizations, organizations => organizations.get('users'))

export const getOrganizationUsersCachedTime = createSelector(getOrganizationUsers, users => users.get('cachedTime'))

export const getUsersOnPage = createSelector(getOrganizationUsers, users => users.get('usersOnPage') || Map()) // TODO delete Map condition when all users switch to store with usersOnPage

export const getAllOrganizationUsers = createSelector(getOrganizationUsers, users => users.get('all'))

export const getOrganizationUsersSortBy = createSelector(getOrganizationUsers, users => users.getIn(['sort', 'by']))

export const getOrganizationUsersSortDesc = createSelector(getOrganizationUsers, users => users.getIn(['sort', 'desc']))

export const getOrganizationUsersFilterBy = createSelector(getOrganizationUsers, users => users.get('filter'))

export const getOrganizationUsersFilterByForAdmin = createSelector(getOrganizationUsers, users =>
  users.get('filterForAdmin')
)

export const getUsersOnPageList = createSelector(getUsersOnPage, users =>
  users.reduce((all: List<any>, user: any) => {
    return all.push(user)
  }, List())
)

export const getUsersOnPageListSorted = createSelector(
  getUsersOnPageList,
  getOrganizationUsersSortBy,
  getOrganizationUsersSortDesc,
  (usersOnPage, by, desc) => {
    // usersOnPage is List from Map object so sorting is not correct that's why recovering backend sorting is needed
    if (by && desc) {
      return usersOnPage.sortBy(val => {
        if (desc === 0) {
          return val.get(by)
        } else {
          return -val.get(by)
        }
      })
    }
    return usersOnPage
  }
)

export const getOrganizationUsersList = createSelector(getAllOrganizationUsers, users =>
  users.reduce((all: any, user: any) => {
    return all.push(user)
  }, List())
)

export const getOrganizationUsersListFilteredBy = createSelector(
  getOrganizationUsersList,
  getOrganizationUsersFilterByForAdmin,
  (list, bySearch) => {
    return selectorHelpers.filterBySearh(list, bySearch)
  }
)

export const getOrganizationUsersFilterByTeam = createSelector(getOrganizationUsers, users => users.get('filterByTeam'))

export const getOrganizationUsersListFilteredByTeam = createSelector(
  getOrganizationUsersList,
  getOrganizationUsersFilterByTeam,
  selectorHelpers.filterByTeam
)

export const getOrganizationUsersNotInvitationAccepted = createSelector(
  getOrganizationUsersListFilteredByTeam,
  selectorHelpers.filterUsersNotInvitationAccepted
)

export const getOrganizationUsersNotInvitationAcceptedEmails = createSelector(
  getOrganizationUsersNotInvitationAccepted,
  selectorHelpers.takeEmailsUsersFromUsersList
)

const getOrganizationUsersNotHaveActiveProTrack = createSelector(
  getOrganizationUsersListFilteredByTeam,
  selectorHelpers.filterUsersNotProTrackPicked
)

export const getOrganizationUsersStoppedProTrack = createSelector(
  getOrganizationUsersNotHaveActiveProTrack,
  (users: any) => {
    return users.filter((user: any) => user.get('isUserProtrackLevelsExist'))
  }
)

export const getOrganizationUsersLevelFinished = createSelector(
  getOrganizationUsersNotHaveActiveProTrack,
  (users: any) => {
    return users.filter((user: any) => user.get('levelFinished'))
  }
)

export const getOrganizationUsersLevelStopped = createSelector(
  getOrganizationUsersNotHaveActiveProTrack,
  (users: any) => {
    return users.filter((user: any) => user.get('levelStopped'))
  }
)

export const getOrganizationUsersLevelFinishedEmails = createSelector(getOrganizationUsersLevelFinished, (users: any) =>
  users.reduce((users: any, user: any) => users.push(user.get('email')), List())
)

export const getOrganizationUsersLevelStoppedEmails = createSelector(getOrganizationUsersLevelStopped, (users: any) =>
  users.reduce((users: any, user: any) => users.push(user.get('email')), List())
)

export const getOrganizationUsersStoppedEmails = createSelector(getOrganizationUsersStoppedProTrack, (users: any) =>
  users.reduce((users: any, user: any) => users.push(user.get('email')), List())
)

export const getOrganizationUsersNotPickedProTrack = createSelector(
  getOrganizationUsersNotHaveActiveProTrack,
  (users: any) => {
    return users.filter((user: any) => !user.get('isUserProtrackLevelsExist'))
  }
)

export const getOrganizationUsersNotProTrackPickedEmails = createSelector(
  getOrganizationUsersNotPickedProTrack,
  selectorHelpers.takeEmailsUsersFromUsersList
)

export const getOrganizationUsersNotProTrackPickedList = createSelector(
  getOrganizationUsersNotInvitationAccepted,
  (users: any) => users.reduce((all: any, user: any) => all.push(user), List())
)

export const getOrganizationUsersNotInvitationAcceptedList = createSelector(
  getOrganizationUsersNotPickedProTrack,
  (users: any) => users.reduce((all: any, user: any) => all.push(user), List())
)

export const getOrganizationUsersFilter = createSelector(getOrganizationUsers, users => users.get('usersFilter'))

const getOrganizationUsersListFilteredByStatus = createSelector(
  getOrganizationUsersListFilteredByTeam,
  getOrganizationUsersNotInvitationAcceptedList,
  getOrganizationUsersNotProTrackPickedList,
  getOrganizationUsersLevelFinished,
  getOrganizationUsersLevelStopped,
  getOrganizationUsersFilter,
  (list, listNotInvitationAccepted, listNotProTrackPicked, listCompletedProTrack, listPausedProTrack, byStatus) => {
    return (
      {
        [USERS_FILTER.POST]: listNotProTrackPicked,
        [USERS_FILTER.FIRE_OUTLINE]: listNotInvitationAccepted,
        [USERS_FILTER.FIRE_OUTLINE_COMPLETED_PROTRACK]: listCompletedProTrack,
        [USERS_FILTER.FIRE_OUTLINE_PAUSED_PROTRACK]: listPausedProTrack,
      }[byStatus] || list
    )
  }
)
export const getOrganizationUsersListFiltered = createSelector(
  getOrganizationUsersListFilteredByStatus,
  getOrganizationUsersFilterBy,
  (list, bySearch) => {
    return selectorHelpers.filterBySearh(list, bySearch)
  }
)

export const getOrganizationUsersListFilteredTotal = createSelector(getOrganizationUsersListFiltered, list => list.size)

export const getOrganizationUsersTab = createSelector(getOrganizationUsers, users => users.get('tab'))

export const getOrganizationUsersisUsersTeamCanBeRemoved = createSelector(getOrganizationUsers, users =>
  users.get('isUsersTeamCanBeRemoved')
)

export const getOrganizationUsersOrganizationId = createSelector(getOrganizationUsers, users =>
  users.get('selectedOrganizationId')
)

export const getOrganizationUsersOrganizationName = createSelector(getOrganizationUsers, users =>
  users.get('selectedOrganizationName')
)

const getOrganizationUsersSelectedOrganization = createSelector(getOrganizationUsers, users =>
  users.get('selectedOrganization')
)

export const isOrganizationDemoMode = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  (organizations, id) => id && organizations.getIn([id.toString(), 'demoMode'])
)

export const getOrganizationUsersSelectedOrganizationAvailableProTrackLevels = createSelector(
  getOrganizationUsersSelectedOrganization,
  selectedOrg => {
    return selectedOrg && selectedOrg.get ? selectedOrg.get('proTrackLevels') : List()
  }
)

export const getSelectedProTrackLevelForSelectedOrganization = (proTrackLevelId: number) =>
  createSelector(getOrganizationUsersSelectedOrganizationAvailableProTrackLevels, levels => {
    return proTrackLevelId ? levels.find((level: any) => level.id === proTrackLevelId) : null
  })

export const getHistorySwitchProTrackLevels = createSelector(getOrganizationUsersSelectedOrganization, selectedOrg => {
  return selectedOrg && selectedOrg.get ? selectedOrg.get('historySwitchProTrackLevels') : List()
})

export const getFirstHistorySwitchProTrackLevels = createSelector(getHistorySwitchProTrackLevels, (list: List<any>) => {
  console.log('_list_', list.toJS())
  return list.first()
})

export const getOrganizationsUsersOrganization = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  getFirstOrganization,
  (organizations, id, organization): Map<string, any> => (id && organizations.get(id.toString())) || organization
)

export const getOrganizationTeams = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  (organization, id) => {
    return organization.getIn([id && id.toString(), 'teams'], List())
  }
)

export const getOrganizationNameById = createSelector(
  getOrganizationUsersOrganizationId,
  getOrganizationsList,
  (id, organizations) => {
    const currentOrg = organizations.find((organization: any) => organization.get('id') === id)
    if (currentOrg) {
      return currentOrg.get('name')
    }
    return null
  }
)

export const getCountriesCodesBySelectedOrganizationId = createSelector(
  getOrganizationUsersOrganizationId,
  getOrganizationsList,
  (id, organizations) => {
    const currentOrg = organizations.find((organization: any) => organization.get('id') === id)
    if (currentOrg) {
      return currentOrg.get('countriesCodes')
    }
    return new CountriesCodes({ [USA_ISO]: true })
  }
)

export const getOrganizationUsersPagination = createSelector(getOrganizationUsers, users => users.get('pagination'))

export const getOrganizationUsersCurrentPage = createSelector(getOrganizationUsersPagination, pagination =>
  pagination.get('current')
)

export const getOrganizationUsersPerPage = createSelector(getOrganizationUsersPagination, pagination =>
  pagination.get('perPage')
)

export const getOrganizationUsersCount = createSelector(getOrganizationUsersPagination, pagination =>
  pagination.get('count')
)

export const getOrganizationUsersCountByStatus = createSelector(getOrganizationUsers, users =>
  users.get('countByStatus')
)

export const getOrganizationUsersTotalPages = createSelector(getOrganizationUsersPagination, pagination =>
  pagination.get('totalPages')
)

export const getOrganizationsForm = createSelector(getOrganizations, organizations => organizations.get('form'))

export const getOrganizationsFormType = createSelector(getOrganizationsForm, form => form.get('type'))

export const getOrganizationFormLevels = createSelector(getPublishedProtracksList, (protracks: any) =>
  Map(
    protracks.reduce((levels: any, protrack: any) => {
      const updatedLevels = protrack.get('levels').reduce((protrackLevels: any, level: any) => {
        protrackLevels[level.get('id')] = Map({
          id: level.get('id'),
          customName: level.get('name'),
          selected: false,
          hasActiveUsers: false,
          proTrackId: level.get('proTrackId'),
        })
        return protrackLevels
      }, {})
      return { ...levels, ...updatedLevels }
    }, {})
  )
)

const getOrganizationFormLibrariesLevels = createSelector(getPublishedProTracksLibrariesList, (proTracks: any) => {
  const res = proTracks.reduce((levels: object, proTrack: any) => {
    const updatedLevels = proTrack.get('libraryLevels').reduce((proTrackLevels: object, level: any) => {
      // @ts-ignore
      proTrackLevels[level.get('id')] = Map({
        id: level.get('id'),
        customName: level.get('name'),
        selected: false,
      })
      return proTrackLevels
    }, {})
    return { ...levels, ...updatedLevels }
  }, {})
  return Map(res)
})

export const getOrganizationCreateFormValues = createSelector(
  getOrganizationFormLevels,
  getOrganizationFormLibrariesLevels,
  (proTracksLevels, proTracksLibrariesLevels) => {
    return Map({
      microactions: false,
      hasLevelWithActiveUsers: false,
      hasIrregularWorkWeek: false,
      proTrackLevelEndsByActivityCount: false,
      proTracksLevels,
      useEmbeddedImages: false,
      social: false,
      sms: false,
      stories: false,
      reactions: false,
      peerInvites: false,
      feedbackReviews: false,
      reviews: false,
      isApiKeyEnabled: false,
      displayName: null,
      emailProviderApiKey: null,
      emailFrom: null,
      reviewLink: null,
      countriesCodes: new CountriesCodes({ [USA_ISO]: true }),
      changeNotificationFrequency: false,
      isPreTrackSurveyEnabled: true,
      isPostTrackSurveyEnabled: true,
      proTracksLibrariesLevels,
      orgWorkWeek: List(WORK_WEEK_DEFAULT),
      tenantId: null,
    })
  }
)

export const getEditingOrganizationFromRouter = createSelector(
  getAllOrganizations,
  editingOrganizationIdFromRouter,
  (organizations, id) => organizations.get(id.toString())
)

export const getEditingOrganizationFormValues = createSelector(
  getEditingOrganizationFromRouter,
  getOrganizationFormLevels,
  getOrganizationFormLibrariesLevels,
  (organization, microActionLevels, libraryLevels) => {
    const updatedMicroActionLevels = organization.get('proTracksLevels').reduce((levels: any, orgLevel: any) => {
      return levels.set(
        orgLevel.get('id').toString(),
        Map({
          id: orgLevel.get('id'),
          customName: orgLevel.get('name'),
          selected: true,
          hasActiveUsers: orgLevel.get('hasActiveUsers'),
          isOrganizationProTrackLevelActive: orgLevel.get('isOrganizationProTrackLevelActive'),
          teams: orgLevel.get('teams'),
        })
      )
    }, microActionLevels)
    const updatedLibrariesLevels = organization.get('proTracksLibrariesLevels').reduce((levels: any, orgLevel: any) => {
      return levels.set(
        orgLevel.get('id').toString(),
        Map({
          id: orgLevel.get('id'),
          customName: orgLevel.get('name'),
          selected: true,
        })
      )
    }, libraryLevels)
    return Map({
      ...organization.toJS(),
      countriesCodes: Map(organization.get('countriesCodes')),
      inactiveNotificationsPeriod: organization.get('inactiveNotificationsPeriod'),
      microactions: organization.get('microactions'),
      hasLevelWithActiveUsers: !!organization.get('proTracksLevels').find((level: any) => level.get('hasActiveUsers')),
      proTracksLevels: updatedMicroActionLevels,
      proTracksLibrariesLevels: updatedLibrariesLevels,
      orgWorkWeek: organization.get('orgWorkWeek'),
    })
  }
)

export const getOrganizationFormSprints = createSelector(getEditingOrganizationFromRouter, organization => {
  return organization && organization.get ? organization.get('sprints') : List()
})

export const getOrganizationsFormDisabled = createSelector(getOrganizationsForm, form => form.get('disabled'))

export const getViewOrganizationSettings = createSelector(getOrganizations, organizations =>
  organizations.get('viewOrganizationSettings')
)

export const getViewOrganizationSettingsLoaded = createSelector(getViewOrganizationSettings, settings =>
  settings.get('loaded')
)

export const getViewOrganizationSettingsEntity = createSelector(getViewOrganizationSettings, settings =>
  settings.get('entity')
)

export const getViewOrganizationSettingsId = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('id')
)

export const getViewOrganizationTeams = createSelector(
  getAllOrganizations,
  getViewOrganizationId,
  (organization, id) => {
    return organization.getIn([id && id.toString(), 'teams'], List())
  }
)

export const getViewOrganizationSettingsName = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('name')
)

export const getViewOrganizationSettingsDomain = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('domain')
)

export const getViewOrganizationSettingsTotalUsers = createSelector(
  getViewOrganizationSettingsEntity,
  entity => entity.get('users').length
)

export const getViewOrganizationSettingsLogo = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('logo')
)

/**
 * @see store/records/organization OneSignal
 * @return {OneSignal}
 */
export const oneSignal = createSelector(getEditingOrganizationFromRouter, org => org.get('oneSignal'))

const getIsPending = createSelector(getOrganizations, org => org.get('isPending'))

export const isPendingSprint = createSelector(getIsPending, isPending => isPending.get('sprint'))

export const getViewUser = createSelector(getOrganizationUsers, users => users.get('user'))

export const getViewUserId = createSelector(getViewUser, user => user && user.get('view'))

export const getViewUserNameOrEmail = createSelector(getUsersOnPage, getViewUserId, (users, id) => {
  const user = users.get(id && id.toString())
  return user && getUserNameOrEmail(user.get('firstName'), user.get('lastName'), user.get('email'))
})

export const getViewUserProtracks = createSelector(getViewUser, user => user && user.get('protracks'))

export const getUserOnPage = createSelector(
  getUsersOnPage,
  getViewUserId,
  (users, userId) => userId && users.get(userId.toString())
)

export const getUserOnPageTimezone = createSelector(getUserOnPage, user =>
  user ? user.get('timezone') : 'America/Chicago'
)

export const getUserResendNotificationStatus = createSelector(
  getViewUser,
  user => user && user.get('resendNotificationStatus')
)

export const getViewUserPending = createSelector(getOrganizationUsers, users => users.get('pending'))

export const getViewUserLoaded = createSelector(getOrganizationUsers, users => users.get('loaded'))

export const getViewUserActivities = createSelector(getViewUser, user => user && user.get('activities'))

export const getViewUserChanges = createSelector(getViewUser, user => user && user.get('changes'))

export const getViewUserLevelName = createSelector(getViewUser, user => user.get('levelName'))

export const getViewUserProTrackLevelId = createSelector(getViewUser, user => user && user.get('proTrackLevelId'))

export const getViewUserProTrackLevel = createSelector(
  getViewUserProtracks,
  getViewUserProTrackLevelId,
  (protracks, proTrackLevelId) => {
    return protracks.find((protrack: any) => protrack.get('userProTrackLevelId') === proTrackLevelId)
  }
)

export const getViewUserPublicProTrackName = createSelector(getViewUser, user => user.get('publicProTrackName'))

export const isSMSAvailableForOrganization = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  (organization, id) => id && organization.getIn([id.toString(), 'sms'])
)

export const isSMSAvailableForOrganizationFromRouter = createSelector(getEditingOrganizationFromRouter, organization =>
  organization ? organization.get('sms') : false
)

export const isReviewsAvailableForOrganization = createSelector(getOrganizationsUsersOrganization, organization =>
  organization ? organization.get('reviews') : false
)

export const isMicroActionsAvailableForOrganization = createSelector(getOrganizationsUsersOrganization, organization =>
  organization ? organization.get('microactions') : false
)

export const getInvites = createSelector(getOrganizations, organizations => {
  return organizations.get('invites') || invitesState
})

export const getInvitesOrganization = createSelector(getInvites, invites => {
  return invites.get('organization')
})

export const getInvitesOrganizationTeams = createSelector(getInvitesOrganization, organization => {
  return organization.get('teams')
})

export const getUsedLevelsIdsList = createSelector(getOrganizations, organizations => {
  return organizations.get('usedLevelsIds')
})

export const getHasActiveUsersLevelsIdsList = createSelector(getOrganizations, organizations => {
  return organizations.get('hasActiveUsersLevelsIds')
})

export const getOrganizationTeamsById = (organizationId: number) =>
  createSelector(getAllOrganizations, organizations => {
    return organizations.getIn([organizationId.toString(), 'teams'])
  })

export const getAvailableLanguages = createSelector(getOrganizations, organizations =>
  Object.entries(organizations.get('languages'))
)

export const getOrganizationChildrenList = createSelector(
  [getOrganizationSettingsChildrenList, getAllOrganizations],
  (organizationsIds: any, allOrganization: any) => {
    if (organizationsIds.size === 0) {
      return List()
    }
    return organizationsIds.reduce((all: any, orgId: number) => {
      return all.push(allOrganization.get(orgId.toString()))
    }, List())
  }
)

export const getOrganizationProtrackLevelChangesByOrg = createSelector(getEditingOrganizationFromRouter, organization =>
  organization ? organization.get('levelsChanges') : Map()
)

export const getPartnersLinksByOrganizationId = createSelector(getEditingOrganizationFromRouter, organization => {
  return organization ? organization.get('partnersLinks') : List()
})

export const getOrganizationsListForSuperAdmin = createSelector(getOrganizationsList, organizations =>
  organizations.unshift(
    Map({ id: ACTIVITY_DEFAULT_IMAGE_MESSAGE_ID, domain: i18next.t(`admin:${ACTIVITY_DEFAULT_IMAGE_MESSAGE}`) }),
    Map({ id: ACTIVITY_DEFAULT_IMAGE_DOMAIN_ID, domain: ACTIVITY_DEFAULT_IMAGE_DOMAIN })
  )
)

export const getOrganizationsListForMultiAdmin = createSelector(getOrganizationsListForSuperAdmin, organizations =>
  organizations.delete(1)
)

export const getOrganizationSprint = createSelector(getEditingOrganizationFromRouter, organization => {
  const sprint = organization ? organization.get('sprint') : Map()
  const { start, end } = sprint
  return Map({
    ...sprint,
    dates: Map({
      startDate: moment.tz(start, 'UTC'),
      endDate: moment.tz(end, 'UTC'),
    }),
  })
})

export const getSprintIsStarted = createSelector(getOrganizationSprint, sprint => {
  return sprint.get('isStarted')
})

export const getMicroActionLevelUsersList = createSelector(getOrganizations, organizations => {
  return organizations.get('microActionLevelUsersList')
})

export const getSelectedOrganizationDomain = createSelector(
  [getOrganizationsUsersOrganization],
  OrganizationsUsersOrganization => {
    return OrganizationsUsersOrganization.get('domain')
  }
)

export const getOrganizationByIdPending = createSelector(getIsPending, isPending =>
  isPending.get('getOrganizationById')
)
